import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { NewClientDialog } from "@/components/users/new-client-dialog";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import {
  Link,
  createFileRoute,
  redirect,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/users` — admin-only listing. Port of
 * `src/app/(app)/users/page.tsx`. Same shape: paginated table,
 * search filter on name + email, role badges with the same colour
 * mapping the Next page used.
 *
 * Admin gate: the route's loader hits `/api/users` which is
 * admin-only on the server. On 403 we redirect to /my (which is
 * still Next-owned, so a full nav lands the user on a permitted
 * page). The Next version used `redirect("/dashboard")`; we go to
 * /my instead because /dashboard is also still Next-owned and the
 * UX is the same either way.
 */

const SORT_KEYS = [
  "name",
  "email",
  "role",
  "gitlabUserId",
  "iban",
  "entries",
  "createdAt",
] as const;
type SortKey = (typeof SORT_KEYS)[number];

const usersSearchSchema = z.object({
  q: z.string().optional(),
  includeFormer: z.coerce.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
});

type UsersSearch = z.infer<typeof usersSearchSchema>;

type ListedUser = {
  id: string;
  name: string | null;
  email: string;
  globalRole: "admin" | "accountant" | null;
  gitlabUserId: string | null;
  iban: string | null;
  createdAt: string;
  former: boolean;
  _count: { timeEntries: number };
};

type UsersResponse = {
  users: ListedUser[];
  total: number;
  page: number;
  pageSize: number;
};

const roleVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  admin: "destructive",
  accountant: "default",
};

export const Route = createFileRoute("/_app/users/")({
  validateSearch: usersSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    includeFormer: search.includeFormer,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.q) params.set("q", deps.q);
    if (deps.includeFormer === "1") params.set("includeFormer", "1");
    if (deps.sort) params.set("sort", deps.sort);
    if (deps.dir) params.set("dir", deps.dir);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    const qs = params.toString();
    try {
      return await api.get<UsersResponse>(qs ? `/api/users?${qs}` : "/api/users");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // Non-admin: bounce to /my. `href` does a full browser
        // navigation because /my is still Next-owned during the
        // migration; TanStack's typed `to` can't reach it.
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: UsersPage,
});

function UsersPage() {
  const { t } = useTranslation();
  const { users, total, page, pageSize } = Route.useLoaderData();
  const search: UsersSearch = Route.useSearch();
  const navigate = useNavigate();
  // Admin-only page — layout loader already guarantees the
  // session, no extra context needed here.
  const layout = useLoaderData({ from: "/_app" });
  const isAdmin = layout.user.globalRole === "admin";
  const showFormer = search.includeFormer === "1";
  const viewAsActive = layout.viewAs !== null;

  async function viewAs(userId: string) {
    try {
      await api.post(`/api/admin/view-as/${userId}`, {});
    } finally {
      // Full reload so every route loader refetches under the new
      // identity (the swap happens via cookie on the next request).
      globalThis.location.assign("/");
    }
  }

  // Sort is URL-driven so the ordering applies to the whole result,
  // not just the visible page. The header toggle pushes the new
  // `?sort=&dir=` pair and resets `page` back to 1 — landing on page
  // 12 of a fresh ordering would be confusing.
  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "asc",
    onChange: (key, dir) =>
      navigate({
        to: "/users",
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  const columns: Array<DataTableColumn<ListedUser>> = [
    {
      key: "name",
      header: t("usr.list.columns.name"),
      sortAccessor: (u) => u.name,
      cellClassName: "font-medium",
      cell: (u) => (
        <>
          <Link to="/users/$id" params={{ id: u.id }} className="hover:underline text-primary">
            {u.name}
          </Link>
          {u.former && (
            <Badge variant="secondary" className="ml-2">
              {t("usr.list.former")}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: "email",
      header: t("usr.list.columns.email"),
      sortAccessor: (u) => u.email,
      cellClassName: "text-sm text-muted-foreground",
      cell: (u) => u.email,
    },
    {
      key: "role",
      header: t("usr.list.columns.role"),
      sortAccessor: (u) => u.globalRole,
      cell: (u) =>
        u.globalRole ? (
          <Badge variant={roleVariant[u.globalRole] ?? "outline"}>
            {t(`usr.roleBadge.${u.globalRole}`)}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "gitlabUserId",
      header: t("usr.list.columns.gitlabId"),
      sortAccessor: (u) => u.gitlabUserId,
      cellClassName: "font-mono text-xs text-muted-foreground",
      cell: (u) => u.gitlabUserId ?? "—",
    },
    {
      key: "iban",
      header: t("usr.list.columns.iban"),
      sortAccessor: (u) => u.iban,
      cellClassName: "font-mono text-xs text-muted-foreground",
      cell: (u) => (u.iban ? `${u.iban.slice(0, 8)}…` : "—"),
    },
    {
      key: "entries",
      header: t("usr.list.columns.entries"),
      sortAccessor: (u) => u._count.timeEntries,
      cell: (u) => u._count.timeEntries,
    },
    {
      key: "createdAt",
      header: t("usr.list.columns.joined"),
      sortAccessor: (u) => u.createdAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (u) => <LocalDate value={u.createdAt} />,
    },
    {
      key: "actions",
      header: t("usr.list.columns.actions"),
      hidden: !isAdmin,
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (u) =>
        u.id !== layout.user.id && !viewAsActive ? (
          <button
            type="button"
            onClick={() => void viewAs(u.id)}
            className="rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent"
            title={t("usr.list.viewAsTitle")}
          >
            {t("usr.list.viewAs")}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t("usr.list.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("usr.list.total", { count: total })}
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <SearchInput placeholder={t("usr.list.search")} />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showFormer}
              onChange={(e) =>
                void navigate({
                  to: "/users",
                  search: {
                    ...search,
                    includeFormer: e.target.checked ? "1" : undefined,
                    page: undefined,
                  },
                })
              }
            />
            {t("usr.list.showFormer")}
          </label>
        </div>
        {isAdmin && <NewClientDialog />}
      </div>

      <DataTable
        columns={columns}
        rows={users}
        rowKey={(u) => u.id}
        sort={sort}
        emptyState={t("usr.list.empty")}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="users"
        basePath="/users"
        searchParams={{
          q: search.q,
          includeFormer: search.includeFormer,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
